/*! *********************************************************************
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import React from 'react';
import {
  Edit,
  TextField,
  TextInput,
  SelectInput,
  FormDataConsumer,
  TabbedForm,
  FormTab,
  Toolbar,
  SaveButton,
  DateField,
  DeleteButton,
} from 'react-admin';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import { Link } from 'react-router-dom';
import _map from 'lodash/map';

import validateAsset from './validation';
import TagsInput from '../../components/TagsInput';
import AssetCategories from '../../components/AssetCategories';
import PreviewList from '../../components/PreviewList';
import AssetTypeOnly from '../../components/AssetTypeOnly';
import SliderInput from '../../components/SliderInput';
import ModelExtraData from '../../components/ModelExtraData';
import PhysicalSizeInput from '../../components/PhysicalSizeInput';
import AssetGenerationTypeSelector from '../../components/AssetGenerationTypeSelector';
import DownloadAttachementLink from '../../components/DownloadAttachementLink';
import AttachmentDialog from '../../components/AttachmentDialog';
import AttachmentRevisions from '../../components/AttachmentRevisions';
import {
  ASSET_TYPE,
  DOWNLOAD_FORMATS,
  ASSET_STATUSES,
} from '../../models/asset';
import { SOURCE_URL } from '../../env-config';
import SilentUpdateInput from '../../components/SilentUpdateInput';
import { Divider } from '@mui/material';

const materialAtlasDecal = [
  ASSET_TYPE.MATERIAL,
  ASSET_TYPE.ATLAS,
  ASSET_TYPE.DECAL,
];

const materialAtlasDecalIbl = [...materialAtlasDecal, ASSET_TYPE.IBL];

export default (props) => (
  <Edit title="Asset edition" undoable={false} {...props}>
    <TabbedForm
      toolbar={<CustomToolbar />}
      submitOnEnter={false}
      validate={validateAsset}
    >
      {/*-------------*/}
      {/* Base fields */}
      {/*-------------*/}
      <FormTab label="Base fields">
        {/* Type */}
        <TextField label="Type" source="assetTypeLabel" />

        {/* Title */}
        <TextInput label="Title" source="title" />

        {/* Status */}
        <SelectInput
          label="Status"
          source="status"
          choices={_map(ASSET_STATUSES, (s) => ({ name: s[0], id: s[1] }))}
        />
        <Divider fullWidth style={{ margin: '25px 0' }} />

        {/* Category */}
        <AssetCategories label="Categories" source="categories" />

        {/* Tags */}
        <TagsInput label="Tags" source="tags" />

        <Divider fullWidth style={{ margin: '25px 0' }} />
        {/* Cost */}
        <SliderInput label="Cost" source="cost" step={1} min={0} max={10} />
        <Divider fullWidth style={{ margin: '25px 0' }} />
        <SilentUpdateInput
          label="Silent Update"
          source="extraData.silentUpdate"
        />
      </FormTab>

      {/*--------------------*/}
      {/* Rendering controls */}
      {/*--------------------*/}
      <FormTab label="Rendering controls">
        <AssetTypeOnly types={materialAtlasDecal}>
          <SelectInput
            source="extraData.previewStyle"
            label="Preview type"
            choices={[
              { id: 'half_sphere', name: 'Half Sphere' },
              { id: 'sub_sphere', name: 'Sphere (without displacement)' },
              { id: 'sphere', name: 'Sphere (with displacement)' },
              { id: 'plane', name: 'Plane with displacement' },
              { id: 'cloth', name: 'Cloth' },
              { id: 'sss', name: 'SSS (for skins)' },
            ]}
          />
        </AssetTypeOnly>
        <AssetTypeOnly types={materialAtlasDecal}>
          <SelectInput
            source="extraData.previewStyleSketchfab"
            label="Sketchfab preview"
            choices={[
              { id: 'sub_sphere', name: 'Sphere' },
              { id: 'plane', name: 'Plane with displacement' },
              { id: 'cloth', name: 'Cloth' },
            ]}
          />
        </AssetTypeOnly>
        <AssetTypeOnly types={materialAtlasDecal}>
          <SliderInput
            source="extraData.previewDisp"
            label="Displacement factor"
            step={0.01}
            min={0}
            max={5}
          />
        </AssetTypeOnly>
      </FormTab>

      {/*----------*/}
      {/* Previews */}
      {/*----------*/}
      <FormTab label="Previews">
        <FormDataConsumer source="previews">
          {({ formData, basePath, ...rest }) => (
            <PreviewList
              assetId={formData.id}
              assetType={formData.type}
              {...rest}
            />
          )}
        </FormDataConsumer>
      </FormTab>

      {/*-------------*/}
      {/* Attachments */}
      {/*-------------*/}
      <FormTab label="Attachments">
        <AttachmentDialog source="attachments" />

        <AssetTypeOnly types={materialAtlasDecalIbl}>
          <AttachmentRevisions source={DOWNLOAD_FORMATS.sbsar} />
        </AssetTypeOnly>

        <AssetTypeOnly types={[ASSET_TYPE.MODEL]}>
          <AttachmentRevisions source={DOWNLOAD_FORMATS.fbx} />
        </AssetTypeOnly>

        <AssetTypeOnly types={[ASSET_TYPE.IBL]}>
          <AttachmentRevisions source={DOWNLOAD_FORMATS.exr} />
        </AssetTypeOnly>

        <AssetTypeOnly types={[ASSET_TYPE.IBL]}>
          <AttachmentRevisions source={DOWNLOAD_FORMATS.stage} />
        </AssetTypeOnly>

        <AssetTypeOnly types={materialAtlasDecal}>
          <AttachmentRevisions source={DOWNLOAD_FORMATS.sbs} />
        </AssetTypeOnly>

        <AssetTypeOnly types={materialAtlasDecal}>
          <AttachmentRevisions source={DOWNLOAD_FORMATS.mdl} />
        </AssetTypeOnly>

        <AssetTypeOnly types={[ASSET_TYPE.MODEL]}>
          <AttachmentRevisions source={DOWNLOAD_FORMATS.glb} />
        </AssetTypeOnly>
      </FormTab>

      {/*-----------*/}
      {/* ExtraData */}
      {/*-----------*/}
      <FormTab label="ExtraData">
        <AssetTypeOnly types={materialAtlasDecalIbl}>
          <AssetGenerationTypeSelector source="extraData.type" />
        </AssetTypeOnly>

        <AssetTypeOnly types={[ASSET_TYPE.IBL]}>
          <TextInput
            label="Substance Location"
            source="extraData.substance_location"
          />
        </AssetTypeOnly>
        <AssetTypeOnly types={[ASSET_TYPE.IBL]}>
          <TextInput
            label="Substance Resolution"
            source="extraData.substance_resolution"
          />
        </AssetTypeOnly>
        <AssetTypeOnly types={materialAtlasDecal}>
          <PhysicalSizeInput
            label="Physical Size (in cm)"
            source="extraData.physicalSize"
          />
        </AssetTypeOnly>

        <AssetTypeOnly types={[ASSET_TYPE.MODEL]}>
          <DownloadAttachementLink source="glb" label="Additional link" />
        </AssetTypeOnly>

        <AssetTypeOnly types={[ASSET_TYPE.MODEL, ASSET_TYPE.SUBSTANCE_MODEL]}>
          <PhysicalSizeInput
            label="Physical Size (m)"
            source="extraData.physicalSize"
          />
        </AssetTypeOnly>

        <ModelExtraData source="extraData.quality" label="Quality" />
        <ModelExtraData source="extraData.meshes" label="Meshes" />
        <ModelExtraData
          source="extraData.counters_#_equivalentTriangles"
          label="Equivalent Triangles"
        />
        <ModelExtraData source="extraData.counters_#_points" label="Points" />
        <ModelExtraData source="extraData.counters_#_lines" label="Lines" />
        <ModelExtraData
          source="extraData.counters_#_triangles"
          label="Triangles"
        />
        <ModelExtraData source="extraData.counters_#_quads" label="Quads" />
        <ModelExtraData
          source="extraData.counters_#_polygons"
          label="Polygons"
        />
        <ModelExtraData source="extraData.counters_#_extra" label="Extra" />

        <TextInput label="Reference" source="extraData.ref" />

        <TextInput label="Author" source="extraData.author" />

        <TextInput label="UploadTag" source="extraData.uploadTag" />
      </FormTab>

      {/*----------*/}
      {/* MetaData */}
      {/*----------*/}
      <FormTab label="MetaData">
        <TextField label="Id" source="id" emptyText="N/A" />
        <DateField source="createdAt" showTime emptyText="N/A" />
        <DateField source="updatedAt" showTime emptyText="N/A" />
      </FormTab>
    </TabbedForm>
  </Edit>
);

const CustomToolbar = (props) => (
  <Toolbar {...props}>
    <SaveButton disabled={props.pristine} />
    <SaveButton
      label="Apply"
      color="secondary"
      redirect={false}
      style={{ marginLeft: '0.5em' }}
      disabled={props.pristine || props.saving}
    />
    <Box ml="0.5em">
      <Button
        variant="outlined"
        component={Link}
        to={{
          pathname: `${SOURCE_URL}/allassets/${props.record.id}`,
        }}
        target="_blank"
        rel="noopener noreferrer"
      >
        View on 3D Assets
      </Button>
    </Box>
    <DeleteButton style={{ marginLeft: 'auto' }} />
  </Toolbar>
);
